
import { createContext, useContext, useEffect, useState } from "react";

type Theme = "dark";

type ThemeProviderProps = {
  children: React.ReactNode;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
  theme: "dark",
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  ...props
}: ThemeProviderProps) {
  const [theme] = useState<Theme>("dark");

  useEffect(() => {
    const root = window.document.documentElement;
    
    root.classList.remove("light");
    root.classList.add("dark");
    
    // Apply explicit dark mode class to body for consistent styling
    document.body.classList.add('dark-mode-active');
    
    // Force repaint to ensure styles apply completely
    document.body.style.transition = "background-color 0.3s ease, color 0.3s ease";
    document.body.style.backgroundColor = "hsl(240, 10%, 3.9%)";
    document.body.style.color = "hsl(0, 0%, 98%)";
  }, []);

  const value = {
    theme,
    setTheme: () => {
      // No-op function, theme can't be changed
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);
  
  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");
  
  return context;
};
